import { printHTML, downloadWord } from '../utils/printHelper';
import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDatePrint } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash, Printer, FileDoc, Buildings } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import FizickoLiceAutocomplete from '@/components/FizickoLiceAutocomplete';
import ClientAutocomplete from '../components/ClientAutocomplete';

const EMPTY = {
  vrsta_zahtjeva: 'ctr_smjestaj',
  client_id: '',
  datum_podnosenja: new Date().toISOString().split('T')[0],
  podnosilac_naziv: '', podnosilac_adresa: '', podnosilac_pib: '', podnosilac_kontakt: '',
  // Novi objekat
  ctr_vrsta_objekta: '', ctr_naziv_objekta: '', ctr_club_oznaka: false,
  ctr_oblik: 'doo', ctr_pretezna_djelatnost_sifra: '', ctr_pretezna_djelatnost_naziv: '',
  ctr_crps_broj: '', ctr_crps_datum: '', ctr_naziv_sjediste: '', ctr_maticni_broj: '',
  ctr_ulica_broj: '', ctr_opstina_drzava: '', ctr_odg_ime_prezime: '', ctr_odg_jmb: '',
  ctr_odg_vrsta_dok: '', ctr_odg_broj_dok: '', ctr_odg_drzava_dok: '',
  ctr_odg_kontakt: '', ctr_odg_pol: 'M',
  // Preduzetnik (alt za pravno lice)
  ctr_pred_ime_prezime: '', ctr_pred_jmb: '', ctr_pred_pol: 'M',
  ctr_pred_dok_vrsta: '', ctr_pred_dok_broj: '', ctr_pred_dok_drzava: '',
  ctr_pred_ulica_broj: '', ctr_pred_opstina_drzava: '', ctr_pred_kontakt: '',
  ctr_osnov_koriscenja: 'svojina', ctr_osnov_drugi: '',
  ctr_obj_opstina_mjesto: '', ctr_obj_ulica_broj: '', ctr_obj_kat_opstina: '',
  ctr_obj_kat_parcela: '', ctr_obj_kat_lokacija: '', ctr_obj_povrsina: '',
  ctr_dep_kat_parcela: '', ctr_dep_kat_lokacija: '', ctr_dep_povrsina: '',
  ctr_lokacija_grad: false, ctr_lokacija_prigrad: false, ctr_lokacija_plaza: false,
  ctr_lokacija_skijaliste: false, ctr_lokacija_nacionalni_park: false, ctr_lokacija_ostalo: '',
  ctr_odg_objekat_ime: '', ctr_odg_objekat_jmb: '', ctr_odg_objekat_pol: 'M',
  ctr_odg_objekat_dok_vrsta: '', ctr_odg_objekat_dok_broj: '', ctr_odg_objekat_dok_drzava: '',
  ctr_odg_objekat_kontakt: '',
  ctr_zap_zene_stalno: '', ctr_zap_zene_povremeno: '',
  ctr_zap_musk_stalno: '', ctr_zap_musk_povremeno: '',
  ctr_period: 'cijela_godina',
  ctr_obj_broj_soba: '', ctr_obj_broj_apart: '', ctr_obj_broj_lezaja: '',
  ctr_dep_broj_soba: '', ctr_dep_broj_apart: '', ctr_dep_broj_lezaja: '',
  ctr_restoran_vrsta: '', ctr_restoran_broj: '', ctr_restoran_kapacitet: '',
  ctr_sadrzaj_kongres: false, ctr_sadrzaj_kongres_kap: '',
  ctr_sadrzaj_spa: false, ctr_sadrzaj_spa_kap: '',
  ctr_sadrzaj_kazino: false, ctr_sadrzaj_kazino_kap: '',
  ctr_sadrzaj_tenis: false, ctr_sadrzaj_tenis_kap: '',
  ctr_sadrzaj_kosarka: false, ctr_sadrzaj_kosarka_kap: '',
  ctr_sadrzaj_odbojka: false, ctr_sadrzaj_odbojka_kap: '',
  ctr_sadrzaj_bazen_otv: false, ctr_sadrzaj_bazen_otv_kap: '',
  ctr_sadrzaj_bazen_zat: false, ctr_sadrzaj_bazen_zat_kap: '',
  ctr_sadrzaj_ostalo: '', ctr_sadrzaj_ostalo_kap: '',
  ctr_parking_otv: '', ctr_parking_natkr: '', ctr_parking_garaza: '', ctr_parking_neob: '',
  ctr_napomena: '', ctr_lice_odg_podaci: '', ctr_mjesto_potpisa: '',
};

export default function CTRPage() {
  const [zahtjevi, setZahtjevi] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const [z, c] = await Promise.all([api.get('/zahtjevi?vrsta=ctr_smjestaj'), api.get('/clients')]);
      setZahtjevi(z.data); setClients(c.data);
    } catch { toast.error('Greška pri učitavanju'); } finally { setLoading(false); }
  };

  const handleClientSelect = (cid) => {
    const c = clients.find(cl => cl.id === cid);
    if (c) setForm(prev => ({
      ...prev, client_id: cid,
      ctr_naziv_sjediste: c.naziv_firme || c.skraceni_naziv || '',
      ctr_ulica_broj: c.adresa || '',
      ctr_opstina_drzava: c.grad ? `${c.grad}, Crna Gora` : 'Crna Gora',
      ctr_odg_kontakt: [c.telefon, c.email].filter(Boolean).join(', '),
      ctr_odg_ime_prezime: c.direktor || '',
      ctr_crps_broj: c.registarski_broj || '',
      ctr_maticni_broj: c.pib || '',
      podnosilac_naziv: c.naziv_firme || c.skraceni_naziv || '',
      podnosilac_adresa: [c.adresa, c.grad].filter(Boolean).join(', '),
      podnosilac_pib: c.pib || '',
      podnosilac_kontakt: [c.telefon, c.email].filter(Boolean).join(', '),
    }));
  };

  useEffect(() => {
    if (isFormOpen && form.client_id && clients.length > 0 && !form.ctr_naziv_sjediste) {
      handleClientSelect(form.client_id);
    }
  }, [isFormOpen, clients]);

  const handleSubmit = async () => {
    if (!form.ctr_naziv_sjediste) { toast.error('Unesite naziv i sjedište subjekta'); return; }
    if (!form.ctr_odg_ime_prezime) { toast.error('Unesite ime i prezime odgovornog lica'); return; }
    try {
      if (editingId) { await api.put(`/zahtjevi/${editingId}`, form); toast.success('Ažurirano'); }
      else { await api.post('/zahtjevi', form); toast.success('Kreirano'); }
      setIsFormOpen(false); setEditingId(null); setForm(EMPTY); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Greška'); }
  };

  const flToCtrCommon = (fl) => {
    const name = `${fl.prezime || ''} ${fl.ime || ''}`.trim();
    // Derive pol from JMB[9..11] (1..499=M, 500..999=Z) when available
    let pol = fl.pol === 'Ž' || fl.pol === 'Z' ? 'Z' : (fl.pol === 'M' ? 'M' : null);
    if (!pol && fl.jmb && fl.jmb.length >= 12) {
      const k = parseInt(fl.jmb.substring(9, 12), 10);
      pol = isFinite(k) && k >= 500 ? 'Z' : 'M';
    }
    return {
      name,
      jmb: fl.jmb || '',
      vrsta_dok: fl.vrsta_dokumenta || (fl.broj_dokumenta ? 'Lična karta' : ''),
      broj_dok: fl.broj_dokumenta || '',
      drzava_dok: fl.dokument_izdat || 'Crna Gora',
      kontakt: [fl.telefon, fl.email].filter(Boolean).join(', '),
      pol: pol || 'M',
    };
  };

  const handleFlOdgovorno = (fl) => {
    const x = flToCtrCommon(fl);
    setForm(prev => ({
      ...prev,
      ctr_odg_ime_prezime: x.name,
      ctr_odg_jmb: x.jmb,
      ctr_odg_vrsta_dok: x.vrsta_dok,
      ctr_odg_broj_dok: x.broj_dok,
      ctr_odg_drzava_dok: x.drzava_dok,
      ctr_odg_kontakt: x.kontakt,
      ctr_odg_pol: x.pol,
    }));
    toast.success(`Izabrano: ${x.name}`);
  };

  const handleFlPreduzetnik = (fl) => {
    const x = flToCtrCommon(fl);
    setForm(prev => ({
      ...prev,
      ctr_pred_ime_prezime: x.name,
      ctr_pred_jmb: x.jmb,
      ctr_pred_dok_vrsta: x.vrsta_dok,
      ctr_pred_dok_broj: x.broj_dok,
      ctr_pred_dok_drzava: x.drzava_dok,
      ctr_pred_kontakt: x.kontakt,
      ctr_pred_pol: x.pol,
    }));
    toast.success(`Izabrano: ${x.name}`);
  };

  const handleFlOdgObjekat = (fl) => {
    const x = flToCtrCommon(fl);
    setForm(prev => ({
      ...prev,
      ctr_odg_objekat_ime: x.name,
      ctr_odg_objekat_jmb: x.jmb,
      ctr_odg_objekat_dok_vrsta: x.vrsta_dok,
      ctr_odg_objekat_dok_broj: x.broj_dok,
      ctr_odg_objekat_dok_drzava: x.drzava_dok,
      ctr_odg_objekat_kontakt: x.kontakt,
      ctr_odg_objekat_pol: x.pol,
    }));
    toast.success(`Izabrano: ${x.name}`);
  };

  const handleEdit = (z) => {
    setEditingId(z.id);
    setForm({ ...EMPTY, ...z });
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati zahtjev?'))) return;
    try { await api.delete(`/zahtjevi/${id}`); toast.success('Obrisano'); fetchData(); } catch { toast.error('Greška'); }
  };

  const buildHtml = (z) => {
    const cb = (v) => v ? '<span class="cb cb-on">&#9745;</span>' : '<span class="cb">&#9744;</span>';
    const rb = (sel) => sel ? '<span class="rb rb-on">&#9679;</span>' : '<span class="rb">&#9675;</span>';
    const isPred = z.ctr_oblik === 'preduzetnik';
    const mjesto = z.ctr_mjesto_potpisa || z.ctr_obj_opstina_mjesto || '';
    const d = z.datum_podnosenja ? new Date(z.datum_podnosenja) : new Date();
    const dd = String(d.getDate()).padStart(2,'0');
    const mm = String(d.getMonth()+1).padStart(2,'0');
    const yyyy = d.getFullYear();

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>CTR - Centralni turistički registar</title>
<style>
@page { size: A4; margin: 8mm 10mm; }
@media print { html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 8.5pt; color: #000; line-height: 1.2; }
.top-title { background: #dbe6f2; border: 1.5px solid #2c4a78; padding: 4px 8px; text-align: center; font-weight: bold; font-size: 9.5pt; text-transform: uppercase; line-height: 1.3; margin-bottom: 4px; }
.instr-box { border: 1px solid #888; padding: 3px 6px; margin-bottom: 4px; font-size: 7.5pt; background: #fafafa; }
.instr-box b { display: block; margin-bottom: 1px; }
.instr-box ul { margin: 0; padding-left: 14px; }
.instr-box li { margin: 0; }
/* DARK BLUE section bar */
.section-bar { background: #2c4a78; color: #fff; padding: 3px 8px; font-weight: bold; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.2px; margin-top: 3px; border: 1px solid #2c4a78; page-break-after: avoid; }
/* LIGHT grey sub-section title */
.sub-title { background: #e9edf2; color: #000; padding: 2px 7px; font-weight: bold; font-size: 8pt; border: 1px solid #b5bec9; border-top: none; page-break-after: avoid; }
table { width: 100%; border-collapse: collapse; font-size: 8.5pt; page-break-inside: avoid; }
td, th { border: 1px solid #2c4a78; padding: 2px 4px; vertical-align: top; }
td.lbl { background: #f0f3f7; font-weight: bold; width: 35%; }
td.val { min-height: 12px; }
td.gov, .gov { background: #d9d9d9 !important; }
.hdr-cell { background: #dbe6f2; font-weight: bold; text-align: center; }
.cb, .rb { font-size: 10pt; font-family: 'DejaVu Sans', Arial, sans-serif; display: inline-block; width: 12px; }
.cb-on { color: #c0392b; font-weight: bold; }
.rb-on { color: #c0392b; font-weight: bold; }
.foot-note { font-size: 7pt; font-style: italic; color: #333; margin: 1px 0 3px 2px; }
.page-break { /* disabled - continuous flow per user request */ }
.grid2 col { width: 50%; }
.grid3 col { width: 33.33%; }
.grid4 col { width: 25%; }
.two-col { display: flex; gap: 3px; }
.two-col > div { flex: 1; }
.kv { display: inline-block; min-width: 45%; padding: 1px 0; }
.sig-block { margin-top: 6px; border: 1px solid #2c4a78; padding: 5px 8px; page-break-inside: avoid; }
.sig-block .line { border-bottom: 1px solid #000; display: inline-block; min-width: 200px; padding: 1px 3px; }
.sig-right { text-align: right; margin-top: 4px; font-size: 7.5pt; color: #444; }
</style></head><body>

<!-- ============ PAGE 1 ============ -->
<div class="top-title">
UGOSTITELJSKI OBJEKTI ZA PRUŽANJE USLUGA SMJEŠTAJA I<br>
USLUGA PRIPREMANJA I USLUŽIVANJA HRANE, PIĆA I NAPITAKA
</div>

<div class="instr-box">
<b>Popunjavanje obrasca:</b>
<ul>
<li>podaci se unose: tekstom (VELIKIM, ŠTAMPANIM SLOVIMA), oznakom „x" u polje ili zaokruživanjem polja</li>
<li>podatke u neosjenčena polja unosi podnosilac zahtjeva za izdavanje odobrenja za obavljanje ugostiteljske djelatnosti</li>
<li>podatke u osjenčena polja unosi organ državne uprave nadležan za poslove turizma / nadležni organ lokalne uprave</li>
</ul>
</div>

<table>
<colgroup><col style="width:40%"><col style="width:30%"><col style="width:15%"><col style="width:15%"></colgroup>
<tr><td rowspan="3" class="lbl">Vrsta i datum unosa podataka u Centralni turistički registar*:</td>
<td class="gov">upis podataka:</td><td class="gov">____ / ____</td><td class="gov">godine</td></tr>
<tr><td class="gov">promjena / dopuna podataka:</td><td class="gov">____ / ____</td><td class="gov">godine</td></tr>
<tr><td class="gov">prestanak važenja rješenja:</td><td class="gov">____ / ____</td><td class="gov">godine</td></tr>
</table>
<div class="foot-note">*popunjava organ državne uprave nadležan za poslove turizma / nadležni organ lokalne uprave</div>

<table>
<colgroup><col style="width:40%"><col style="width:60%"></colgroup>
<tr><td class="lbl">Broj upisa u Centralni turistički registar*:</td><td class="gov">&nbsp;</td></tr>
</table>
<div class="foot-note">*popunjava organ državne uprave nadležan za poslove turizma / nadležni organ lokalne uprave</div>

<table>
<colgroup><col style="width:35%"><col style="width:65%"></colgroup>
<tr><td class="lbl">VRSTA OBJEKTA:</td><td>${z.ctr_vrsta_objekta || '&nbsp;'}</td></tr>
<tr><td class="lbl">NAZIV OBJEKTA:</td><td>${z.ctr_naziv_objekta || z.ctr_naziv_sjediste || '&nbsp;'}</td></tr>
<tr><td class="lbl">"Club" oznaka:</td><td>${cb(z.ctr_club_oznaka)} Da &nbsp;&nbsp; ${cb(!z.ctr_club_oznaka)} Ne</td></tr>
</table>

<div class="section-bar">PODACI O PRIVREDNOM DRUŠTVU, DRUGOM PRAVNOM LICU, PREDUZETNIKU</div>
<table>
<colgroup><col style="width:35%"><col style="width:21.66%"><col style="width:21.66%"><col style="width:21.66%"></colgroup>
<tr><td rowspan="2" class="lbl">Oblik obavljanja privredne djelatnosti:</td>
<td>${cb(z.ctr_oblik === 'preduzetnik')} Preduzetnik</td>
<td>${cb(z.ctr_oblik === 'kd')} Komanditno društvo</td>
<td>${cb(z.ctr_oblik === 'doo')} Društvo sa ograničenom<br>odgovornošću (D.O.O.)</td></tr>
<tr>
<td>${cb(z.ctr_oblik === 'od')} Ortačko društvo</td>
<td>${cb(z.ctr_oblik === 'ad')} Akcionarsko društvo (A.D.)</td>
<td>${cb(z.ctr_oblik === 'strano')} Dio stranog društva</td></tr>
</table>

<table>
<colgroup><col style="width:35%"><col style="width:32.5%"><col style="width:32.5%"></colgroup>
<tr><td rowspan="2" class="lbl">Pretežna djelatnost*:</td>
<td><b>Šifra:</b> ${z.ctr_pretezna_djelatnost_sifra || '&nbsp;'}</td>
<td><b>Naziv:</b> ${z.ctr_pretezna_djelatnost_naziv || '&nbsp;'}</td></tr>
<tr><td colspan="2" class="gov">&nbsp;</td></tr>
<tr><td rowspan="2" class="lbl">Upis u CRPS*:</td>
<td><b>Broj:</b> ${z.ctr_crps_broj || '&nbsp;'}</td>
<td><b>Datum:</b> ${formatDatePrint(z.ctr_crps_datum) || '&nbsp;'}</td></tr>
<tr><td colspan="2" class="gov">&nbsp;</td></tr>
</table>
<div class="foot-note">* popunjava organ državne uprave nadležan za poslove turizma / nadležni organ lokalne uprave</div>

<div class="sub-title">Popunjava ovlašćeni predstavnik privrednog društva ili drugog pravnog lica:</div>
<table>
<colgroup><col style="width:20%"><col style="width:25%"><col style="width:35%"><col style="width:20%"></colgroup>
<tr><td rowspan="4" class="lbl">Naziv i sjedište:</td>
<td class="lbl">Naziv:</td><td colspan="2">${isPred ? '&nbsp;' : (z.ctr_naziv_sjediste || z.podnosilac_naziv || '&nbsp;')}</td></tr>
<tr><td class="lbl">Matični broj*:</td><td colspan="2">${isPred ? '&nbsp;' : (z.ctr_maticni_broj || '&nbsp;')}</td></tr>
<tr><td class="lbl">Ulica i broj:</td><td colspan="2">${isPred ? '&nbsp;' : (z.ctr_ulica_broj || z.podnosilac_adresa || '&nbsp;')}</td></tr>
<tr><td class="lbl">Opština i država:</td><td colspan="2">${isPred ? '&nbsp;' : (z.ctr_opstina_drzava || '&nbsp;')}</td></tr>

<tr><td rowspan="6" class="lbl">Odgovorno lice:</td>
<td class="lbl">Ime i prezime:</td>
<td>${isPred ? '&nbsp;' : (z.ctr_odg_ime_prezime || '&nbsp;')}</td>
<td>Pol: ${rb(!isPred && z.ctr_odg_pol === 'Z')} Ž &nbsp; ${rb(!isPred && z.ctr_odg_pol === 'M')} M</td></tr>
<tr><td class="lbl">JMB:</td><td colspan="2">${isPred ? '&nbsp;' : (z.ctr_odg_jmb || '&nbsp;')}</td></tr>
<tr><td class="lbl">Identifikacioni dokument**:</td><td colspan="2"><b>Vrsta:</b> ${isPred ? '&nbsp;' : (z.ctr_odg_vrsta_dok || '&nbsp;')}</td></tr>
<tr><td></td><td colspan="2"><b>Broj:</b> ${isPred ? '&nbsp;' : (z.ctr_odg_broj_dok || '&nbsp;')}</td></tr>
<tr><td></td><td colspan="2"><b>Država izdavanja:</b> ${isPred ? '&nbsp;' : (z.ctr_odg_drzava_dok || '&nbsp;')}</td></tr>
<tr><td class="lbl">Kontakt telefon/E-mail:</td><td colspan="2">${isPred ? '&nbsp;' : (z.ctr_odg_kontakt || z.podnosilac_kontakt || '&nbsp;')}</td></tr>
</table>
<div class="foot-note">* Matični broj jednak PIB-u &nbsp;&nbsp; ** za strane državljane</div>

<div class="sub-title">Popunjava preduzetnik:</div>
<table>
<colgroup><col style="width:20%"><col style="width:25%"><col style="width:35%"><col style="width:20%"></colgroup>
<tr><td rowspan="8" class="lbl">Ime i adresa:</td>
<td class="lbl">Ime i prezime:</td>
<td>${isPred ? (z.ctr_pred_ime_prezime || z.ctr_odg_ime_prezime || '&nbsp;') : '&nbsp;'}</td>
<td>Pol: ${rb(isPred && (z.ctr_pred_pol || z.ctr_odg_pol) === 'Z')} Ž &nbsp; ${rb(isPred && (z.ctr_pred_pol || z.ctr_odg_pol) === 'M')} M</td></tr>
<tr><td class="lbl">JMB:</td><td colspan="2">${isPred ? (z.ctr_pred_jmb || z.ctr_odg_jmb || '&nbsp;') : '&nbsp;'}</td></tr>
<tr><td class="lbl">Identifikacioni dokument*:</td><td colspan="2"><b>Vrsta:</b> ${isPred ? (z.ctr_pred_dok_vrsta || z.ctr_odg_vrsta_dok || '&nbsp;') : '&nbsp;'}</td></tr>
<tr><td></td><td colspan="2"><b>Broj:</b> ${isPred ? (z.ctr_pred_dok_broj || z.ctr_odg_broj_dok || '&nbsp;') : '&nbsp;'}</td></tr>
<tr><td></td><td colspan="2"><b>Država izdavanja:</b> ${isPred ? (z.ctr_pred_dok_drzava || z.ctr_odg_drzava_dok || '&nbsp;') : '&nbsp;'}</td></tr>
<tr><td class="lbl">Ulica i broj:</td><td colspan="2">${isPred ? (z.ctr_pred_ulica_broj || z.ctr_ulica_broj || z.podnosilac_adresa || '&nbsp;') : '&nbsp;'}</td></tr>
<tr><td class="lbl">Opština i država:</td><td colspan="2">${isPred ? (z.ctr_pred_opstina_drzava || z.ctr_opstina_drzava || '&nbsp;') : '&nbsp;'}</td></tr>
<tr><td class="lbl">Kontakt telefon/e-mail:</td><td colspan="2">${isPred ? (z.ctr_pred_kontakt || z.ctr_odg_kontakt || z.podnosilac_kontakt || '&nbsp;') : '&nbsp;'}</td></tr>
</table>
<div class="foot-note">* za strane državljane</div>

<!-- ============ OSNOV PRAVA KORIŠĆENJA ============ -->
<div class="section-bar">OSNOV PRAVA KORIŠĆENJA</div>
<table>
<colgroup><col style="width:30%"><col style="width:35%"><col style="width:35%"></colgroup>
<tr><td rowspan="3" class="lbl">Osnov prava korišćenja objekta:</td>
<td colspan="2">${cb(z.ctr_osnov_koriscenja === 'svojina')} Svojina</td></tr>
<tr><td colspan="2">${cb(z.ctr_osnov_koriscenja === 'zakup')} Zakup</td></tr>
<tr><td>${cb(z.ctr_osnov_koriscenja === 'drugi')} Drugi pravni osnov:</td>
<td><b>Navesti osnov:</b> ${z.ctr_osnov_drugi || '&nbsp;'}</td></tr>
<tr><td class="lbl">Opština i mjesto:</td><td colspan="2">${z.ctr_obj_opstina_mjesto || '&nbsp;'}</td></tr>
<tr><td class="lbl">Ulica i broj:</td><td colspan="2">${z.ctr_obj_ulica_broj || '&nbsp;'}</td></tr>
<tr><td class="lbl">Kat. opština:</td><td colspan="2">${z.ctr_obj_kat_opstina || '&nbsp;'}</td></tr>
<tr><td rowspan="3" class="lbl">Za objekat:</td>
<td class="lbl">Broj kat. parcele:</td><td>${z.ctr_obj_kat_parcela || '&nbsp;'}</td></tr>
<tr><td class="lbl">Kat. lokacija:</td><td>${z.ctr_obj_kat_lokacija || '&nbsp;'}</td></tr>
<tr><td class="lbl">Površina:</td><td>${z.ctr_obj_povrsina || '&nbsp;'} m²</td></tr>
<tr><td rowspan="3" class="lbl">Za depadans (ukoliko postoji):</td>
<td class="lbl">Broj kat. parcele:</td><td>${z.ctr_dep_kat_parcela || '&nbsp;'}</td></tr>
<tr><td class="lbl">Kat. lokacija:</td><td>${z.ctr_dep_kat_lokacija || '&nbsp;'}</td></tr>
<tr><td class="lbl">Površina:</td><td>${z.ctr_dep_povrsina || '&nbsp;'} m²</td></tr>
<tr><td rowspan="6" class="lbl">Lokacija/mjesto:</td>
<td colspan="2">${cb(z.ctr_lokacija_grad)} U gradu</td></tr>
<tr><td colspan="2">${cb(z.ctr_lokacija_prigrad)} U prigradskom području</td></tr>
<tr><td colspan="2">${cb(z.ctr_lokacija_plaza)} U blizini plaže</td></tr>
<tr><td colspan="2">${cb(z.ctr_lokacija_skijaliste)} U blizini skijališta</td></tr>
<tr><td colspan="2">${cb(z.ctr_lokacija_nacionalni_park)} U blizini nacionalnog parka</td></tr>
<tr><td colspan="2">${cb(!!z.ctr_lokacija_ostalo)} Ostalo:* ${z.ctr_lokacija_ostalo || '&nbsp;'}</td></tr>
</table>
<div class="foot-note">*upisati lokaciju</div>

<div class="sub-title">Odgovorno lice u objektu:</div>
<table>
<colgroup><col style="width:20%"><col style="width:25%"><col style="width:35%"><col style="width:20%"></colgroup>
<tr><td rowspan="6" class="lbl">Odgovorno lice u objektu:</td>
<td class="lbl">Ime i prezime:</td>
<td>${z.ctr_odg_objekat_ime || '&nbsp;'}</td>
<td>Pol: ${rb((z.ctr_odg_objekat_pol || 'M') === 'Z')} Ž &nbsp; ${rb((z.ctr_odg_objekat_pol || 'M') === 'M')} M</td></tr>
<tr><td class="lbl">JMB:</td><td colspan="2">${z.ctr_odg_objekat_jmb || '&nbsp;'}</td></tr>
<tr><td class="lbl">Identifikacioni dokument*:</td><td colspan="2"><b>Vrsta:</b> ${z.ctr_odg_objekat_dok_vrsta || '&nbsp;'}</td></tr>
<tr><td></td><td colspan="2"><b>Broj:</b> ${z.ctr_odg_objekat_dok_broj || '&nbsp;'}</td></tr>
<tr><td></td><td colspan="2"><b>Država izdavanja:</b> ${z.ctr_odg_objekat_dok_drzava || '&nbsp;'}</td></tr>
<tr><td class="lbl">Kontakt telefon/E-mail:</td><td colspan="2">${z.ctr_odg_objekat_kontakt || '&nbsp;'}</td></tr>
</table>
<div class="foot-note">*za strane državljane</div>

<table>
<colgroup><col style="width:22%"><col style="width:13%"><col style="width:13%"><col style="width:13%"><col style="width:13%"><col style="width:13%"><col style="width:13%"></colgroup>
<tr><td rowspan="4" class="lbl">Struktura i broj zaposlenih lica:</td>
<td rowspan="2" class="hdr-cell" colspan="3">Stalno:</td>
<td rowspan="2" class="hdr-cell" colspan="3">Povremeno:</td></tr><tr></tr>
<tr><td class="lbl">žene:</td><td>${z.ctr_zap_zene_stalno || '&nbsp;'}</td><td class="lbl">muškarci:</td><td>${z.ctr_zap_musk_stalno || '&nbsp;'}</td>
<td class="lbl">žene:</td><td>${z.ctr_zap_zene_povremeno || '&nbsp;'}</td></tr>
<tr><td colspan="2"><b>ukupno:</b> ${((+z.ctr_zap_zene_stalno||0)+(+z.ctr_zap_musk_stalno||0)) || '&nbsp;'}</td>
<td class="lbl">muškarci:</td><td>${z.ctr_zap_musk_povremeno || '&nbsp;'}</td>
<td colspan="2"><b>ukupno:</b> ${((+z.ctr_zap_zene_povremeno||0)+(+z.ctr_zap_musk_povremeno||0)) || '&nbsp;'}</td></tr>
</table>

<table>
<colgroup><col style="width:25%"><col style="width:25%"><col style="width:25%"><col style="width:25%"></colgroup>
<tr><td class="lbl">Period rada:</td>
<td>${cb(z.ctr_period === 'cijela_godina')} Tokom cijele godine</td>
<td>${cb(z.ctr_period === 'sezonski_ljeti')} Sezonski – ljeti</td>
<td>${cb(z.ctr_period === 'sezonski_zimi')} Sezonski – zimi</td></tr>
</table>

<table>
<colgroup><col style="width:40%"><col style="width:20%"><col style="width:20%"><col style="width:20%"></colgroup>
<tr><td rowspan="3" class="lbl">Smještajne jedinice/vrste i kapacitet:</td>
<td class="hdr-cell">Broj soba:</td><td class="hdr-cell">Broj apartmana:</td><td class="hdr-cell">Broj ležaja:</td></tr>
<tr><td>${z.ctr_obj_broj_soba || '&nbsp;'}</td><td>${z.ctr_obj_broj_apart || '&nbsp;'}</td><td>${z.ctr_obj_broj_lezaja || '&nbsp;'}</td></tr>
<tr><td colspan="3"><b>U depadansu/ima*:</b> Sobe: ${z.ctr_dep_broj_soba||'_'} &nbsp; Apartmani: ${z.ctr_dep_broj_apart||'_'} &nbsp; Ležaji: ${z.ctr_dep_broj_lezaja||'_'}</td></tr>
</table>
<div class="foot-note">*ukoliko postoje</div>

<table>
<colgroup><col style="width:25%"><col style="width:25%"><col style="width:25%"><col style="width:25%"></colgroup>
<tr><td class="lbl" rowspan="2">Restoran/i:</td><td class="hdr-cell">Vrsta:</td><td class="hdr-cell">Broj:</td><td class="hdr-cell">Kapacitet*:</td></tr>
<tr><td>${z.ctr_restoran_vrsta || '&nbsp;'}</td><td>${z.ctr_restoran_broj || '&nbsp;'}</td><td>${z.ctr_restoran_kapacitet || '&nbsp;'}</td></tr>
</table>
<div class="foot-note">*broj sjedećih mjesta</div>

<table>
<colgroup><col style="width:40%"><col style="width:15%"><col style="width:15%"><col style="width:30%"></colgroup>
<tr><td class="lbl">Sadržaji:</td><td class="hdr-cell">Da</td><td class="hdr-cell">Ne</td><td class="hdr-cell">Kapacitet/veličina*:</td></tr>
${[
  ['Kongresna sala:', 'ctr_sadrzaj_kongres', 'ctr_sadrzaj_kongres_kap'],
  ['Velnes/spa:', 'ctr_sadrzaj_spa', 'ctr_sadrzaj_spa_kap'],
  ['Kazino:', 'ctr_sadrzaj_kazino', 'ctr_sadrzaj_kazino_kap'],
  ['Teniski teren:', 'ctr_sadrzaj_tenis', 'ctr_sadrzaj_tenis_kap'],
  ['Košarkaški teren:', 'ctr_sadrzaj_kosarka', 'ctr_sadrzaj_kosarka_kap'],
  ['Tereni za odbojku:', 'ctr_sadrzaj_odbojka', 'ctr_sadrzaj_odbojka_kap'],
  ['Otvoreni bazen:', 'ctr_sadrzaj_bazen_otv', 'ctr_sadrzaj_bazen_otv_kap'],
  ['Zatvoreni bazen:', 'ctr_sadrzaj_bazen_zat', 'ctr_sadrzaj_bazen_zat_kap'],
].map(([lbl, k, kk]) => `<tr><td class="lbl">${lbl}</td><td style="text-align:center">${cb(!!z[k])}</td><td style="text-align:center">${cb(!z[k])}</td><td>${z[kk] || '&nbsp;'}</td></tr>`).join('')}
<tr><td class="lbl">Ostalo**:</td><td style="text-align:center">${cb(!!z.ctr_sadrzaj_ostalo)}</td><td style="text-align:center">${cb(!z.ctr_sadrzaj_ostalo)}</td><td>${z.ctr_sadrzaj_ostalo || '&nbsp;'}</td></tr>
</table>
<div class="foot-note">*broj sjedećih mjesta/veličina i sl. &nbsp;&nbsp; ** upisati ostale sadržaje</div>

<!-- ============ RJEŠENJA (popunjava organ) ============ -->

<table>
<colgroup><col style="width:30%"><col style="width:35%"><col style="width:35%"></colgroup>
<tr><td class="lbl" rowspan="5">Parking prostor:</td><td class="hdr-cell">Vrsta:</td><td class="hdr-cell">Kapacitet/broj mjesta:</td></tr>
<tr><td class="lbl">Otvoreni:</td><td>${z.ctr_parking_otv || '&nbsp;'}</td></tr>
<tr><td class="lbl">Natkriveni:</td><td>${z.ctr_parking_natkr || '&nbsp;'}</td></tr>
<tr><td class="lbl">Garaža:</td><td>${z.ctr_parking_garaza || '&nbsp;'}</td></tr>
<tr><td class="lbl">Neobelježeni:</td><td>${z.ctr_parking_neob || '&nbsp;'}</td></tr>
</table>

<div class="section-bar">RJEŠENJE O ODOBRENJU ZA OBAVLJANJE UGOSTITELJSKE DJELATNOSTI*</div>
<table>
<colgroup><col style="width:40%"><col style="width:60%"></colgroup>
<tr><td class="lbl">Naziv organa koji je izdao rješenje:</td><td class="gov">&nbsp;</td></tr>
<tr><td class="lbl">Broj rješenja:</td><td class="gov">&nbsp;</td></tr>
<tr><td class="lbl">Datum izdavanja rješenja:</td><td class="gov">&nbsp;</td></tr>
<tr><td class="lbl">Rješenje važi do:</td><td class="gov">&nbsp;</td></tr>
</table>
<div class="foot-note">*podatke popunjava organ državne uprave nadležan za poslove turizma / nadležni organ lokalne uprave</div>

<div class="section-bar">RJEŠENJE O KATEGORIZACIJI / RE-KATEGORIZACIJI*</div>
<table>
<colgroup><col style="width:30%"><col style="width:16%"><col style="width:16%"><col style="width:38%"></colgroup>
<tr><td class="lbl" rowspan="2">Za objekat:</td><td class="lbl">Broj:</td><td class="lbl">Datum:</td><td class="lbl">Rok važenja do:</td></tr>
<tr><td class="gov">&nbsp;</td><td class="gov">&nbsp;</td><td class="gov">&nbsp;</td></tr>
<tr><td class="lbl">Nivo kategorije:</td><td class="gov" colspan="3" style="text-align:center">1&#9733; &nbsp;&nbsp; 2&#9733;&#9733; &nbsp;&nbsp; 3&#9733;&#9733;&#9733; &nbsp;&nbsp; 4&#9733;&#9733;&#9733;&#9733; &nbsp;&nbsp; 5&#9733;&#9733;&#9733;&#9733;&#9733;</td></tr>
<tr><td class="lbl" rowspan="2">Za depadans/e (ukoliko postoji):</td><td class="lbl">Broj:</td><td class="lbl">Datum:</td><td class="lbl">Rok važenja do:</td></tr>
<tr><td class="gov">&nbsp;</td><td class="gov">&nbsp;</td><td class="gov">&nbsp;</td></tr>
<tr><td class="lbl">Nivo kategorije:</td><td class="gov" colspan="3" style="text-align:center">1&#9733; &nbsp;&nbsp; 2&#9733;&#9733; &nbsp;&nbsp; 3&#9733;&#9733;&#9733; &nbsp;&nbsp; 4&#9733;&#9733;&#9733;&#9733; &nbsp;&nbsp; 5&#9733;&#9733;&#9733;&#9733;&#9733;</td></tr>
</table>
<div class="foot-note">*popunjava nadležni organ samo za vrstu objekta koji se kategoriše</div>

<table>
<colgroup><col style="width:35%"><col style="width:65%"></colgroup>
<tr><td class="lbl">Specijalizacija*:</td><td class="gov">&nbsp;</td></tr>
</table>
<div class="foot-note">*popunjava nadležni organ</div>

<div class="section-bar">ĆUTANJE ADMINISTRACIJE (istek roka)*</div>
<table>
<colgroup><col style="width:45%"><col style="width:27.5%"><col style="width:27.5%"></colgroup>
<tr><td class="lbl" rowspan="2">Obavještenje o početku obavljanja djelatnosti:</td>
<td class="lbl">Broj:</td><td class="lbl">Datum:</td></tr>
<tr><td class="gov">&nbsp;</td><td class="gov">&nbsp;</td></tr>
</table>
<div class="foot-note">*u slučaju da je obavljanje djelatnosti počelo po osnovu „ćutanja administracije", odnosno isteka roka za odlučivanje po podnešenom zahtjevu - podatke popunjava organ državne uprave nadležan za poslove turizma / nadležni organ lokalne uprave</div>

<table>
<colgroup><col style="width:35%"><col style="width:65%"></colgroup>
<tr><td class="lbl">Napomena (ostale informacije/komentar i sl.):</td><td style="min-height:40px">${z.ctr_napomena || '&nbsp;'}</td></tr>
</table>

<div class="sig-block">
<div><b>Lice odgovorno za tačnost podataka:</b></div>
<div style="margin-top:10px">
<span class="line">${z.ctr_lice_odg_podaci || z.ctr_odg_ime_prezime || '&nbsp;'}</span>
<span style="margin-left:10px">(ime i prezime)</span>
</div>
<div style="margin-top:18px">
<span class="line">&nbsp;</span>
<span style="margin-left:10px">(potpis)</span>
</div>
<div style="margin-top:14px">
U <span class="line" style="min-width:180px">${mjesto}</span>, dana, <span class="line" style="min-width:40px">${dd}</span> / <span class="line" style="min-width:40px">${mm}</span> / <span class="line" style="min-width:50px">${yyyy}</span> god.
</div>
</div>

<div class="sig-right">Kreirao: ${z.created_by_name || 'N/A'} | FINANCE GROUP CRM</div>

<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
  };

  const handlePrint = (z) => printHTML(buildHtml(z));
  const handleWord = (z) => downloadWord(buildHtml(z), `CTR_${(z.ctr_naziv_sjediste||z.podnosilac_naziv||'zahtjev').replace(/\s+/g,'_')}.doc`);

  return (
    <div className="space-y-6" data-testid="ctr-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">CTR - Ugostiteljski objekti</h3>
          <p className="text-sm text-slate-600 mt-1">Centralni turistički registar — smještaj, hrana, piće</p>
        </div>
        <Button onClick={() => {setForm(EMPTY);setEditingId(null);setIsFormOpen(true);}} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ctr-add-btn">
          <Plus size={20} weight="bold" className="mr-2" /> Novi CTR zahtjev
        </Button>
      </div>
      {loading ? <p className="text-center text-slate-500 py-16">Učitavanje...</p> : zahtjevi.length === 0 ? (
        <Card className="p-8 text-center">
          <Buildings size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">Nema CTR zahtjeva</p>
        </Card>
      ) : (
        <Card className="overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-left text-sm text-slate-600" data-testid="ctr-table"><thead className="bg-slate-50 border-b"><tr>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Subjekat</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Oblik</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Objekat lokacija</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Formirao</th>
          <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
        </tr></thead><tbody>{zahtjevi.map(z=>(
          <tr key={z.id} className="border-b hover:bg-slate-50">
            <td className="px-4 py-3 text-xs">{formatDatePrint(z.datum_podnosenja)||'-'}</td>
            <td className="px-4 py-3 font-medium">{z.ctr_naziv_sjediste||z.podnosilac_naziv||'-'}</td>
            <td className="px-4 py-3 uppercase text-xs">{z.ctr_oblik||'-'}</td>
            <td className="px-4 py-3 text-xs">{z.ctr_obj_opstina_mjesto||'-'}</td>
            <td className="px-4 py-3 text-sm text-slate-600">{z.created_by_name||'-'}</td>
            <td className="px-4 py-3"><div className="flex gap-1">
              <button onClick={()=>handlePrint(z)} className="p-2 hover:bg-green-100 rounded" title="Štampaj"><Printer size={16} className="text-green-600"/></button>
              <button onClick={()=>handleWord(z)} className="p-2 hover:bg-indigo-100 rounded" title="Word dokument"><FileDoc size={16} className="text-indigo-600"/></button>
              <button onClick={()=>handleEdit(z)} className="p-2 hover:bg-slate-200 rounded" title="Uredi"><Pencil size={16} className="text-slate-600"/></button>
              <button onClick={()=>handleDelete(z.id)} className="p-2 hover:bg-red-100 rounded" title="Obriši"><Trash size={16} className="text-red-600"/></button>
            </div></td>
          </tr>))}</tbody></table></div></Card>
      )}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId?'Uredi CTR zahtjev':'Novi CTR - Ugostiteljski objekti'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <Card className="p-3 bg-blue-50 border-blue-200">
              <Label className="text-xs font-semibold uppercase text-blue-700 mb-2 block">Auto-popunjavanje iz klijenta</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => (v=>v!=='none'&&handleClientSelect(v))(id || 'none')} placeholder="Pretraga po nazivu ili PIB-u..." />
            </Card>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2">Osnovni podaci o objektu</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold uppercase">Vrsta objekta *</Label>
                <Input value={form.ctr_vrsta_objekta} onChange={e => setForm({...form, ctr_vrsta_objekta: e.target.value})} placeholder="npr. Hotel, Pansion, Restoran..." className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 font-semibold uppercase">Naziv objekta</Label>
                <Input value={form.ctr_naziv_objekta} onChange={e => setForm({...form, ctr_naziv_objekta: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.ctr_club_oznaka} onChange={e => setForm({...form, ctr_club_oznaka: e.target.checked})}/>
              "Club" oznaka
            </label>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2">1. Podaci o privrednom subjektu</h4>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 font-semibold uppercase">Oblik obavljanja djelatnosti *</Label>
              <select value={form.ctr_oblik} onChange={e => setForm({...form, ctr_oblik: e.target.value})} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                <option value="preduzetnik">Preduzetnik</option>
                <option value="kd">Komanditno društvo</option>
                <option value="ad">Akcionarsko društvo (A.D.)</option>
                <option value="doo">D.O.O.</option>
                <option value="strano">Dio stranog društva</option>
                <option value="od">Ortačko društvo</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Pretežna djelatnost - šifra</Label>
                <Input value={form.ctr_pretezna_djelatnost_sifra} onChange={e => setForm({...form, ctr_pretezna_djelatnost_sifra: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Pretežna djelatnost - naziv</Label>
                <Input value={form.ctr_pretezna_djelatnost_naziv} onChange={e => setForm({...form, ctr_pretezna_djelatnost_naziv: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">CRPS - broj</Label>
                <Input value={form.ctr_crps_broj} onChange={e => setForm({...form, ctr_crps_broj: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">CRPS - datum</Label>
                <DateInput value={form.ctr_crps_datum} onChange={e => setForm({...form, ctr_crps_datum: e.target.value})} className="border-slate-300 text-sm"/></div>
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">2. Naziv i sjedište</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Naziv i sjedište *</Label>
                <Input value={form.ctr_naziv_sjediste} onChange={e => setForm({...form, ctr_naziv_sjediste: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Matični broj (PIB)</Label>
                <Input value={form.ctr_maticni_broj} onChange={e => setForm({...form, ctr_maticni_broj: e.target.value})} maxLength={8} className="bg-white border-slate-300 text-sm font-mono"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Ulica i broj</Label>
                <Input value={form.ctr_ulica_broj} onChange={e => setForm({...form, ctr_ulica_broj: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Opština i država</Label>
                <Input value={form.ctr_opstina_drzava} onChange={e => setForm({...form, ctr_opstina_drzava: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">3. Odgovorno lice</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Ime i prezime *</Label>
                <FizickoLiceAutocomplete
                  value={form.ctr_odg_ime_prezime}
                  onChange={v => setForm({...form, ctr_odg_ime_prezime: v})}
                  onSelect={handleFlOdgovorno}
                  clientId={form.client_id}
                  testid="ctr-odg-ime-input"
                /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">JMB</Label>
                <Input value={form.ctr_odg_jmb} onChange={e => setForm({...form, ctr_odg_jmb: e.target.value.replace(/\D/g,'').slice(0,13)})} maxLength={13} className="bg-white border-slate-300 text-sm font-mono"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Dok. vrsta</Label>
                <Input value={form.ctr_odg_vrsta_dok} onChange={e => setForm({...form, ctr_odg_vrsta_dok: e.target.value})} placeholder="Lična karta / Pasoš" className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Broj dokumenta</Label>
                <Input value={form.ctr_odg_broj_dok} onChange={e => setForm({...form, ctr_odg_broj_dok: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Država izdavanja</Label>
                <Input value={form.ctr_odg_drzava_dok} onChange={e => setForm({...form, ctr_odg_drzava_dok: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Kontakt tel./email</Label>
                <Input value={form.ctr_odg_kontakt} onChange={e => setForm({...form, ctr_odg_kontakt: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Pol</Label>
                <select value={form.ctr_odg_pol} onChange={e => setForm({...form, ctr_odg_pol: e.target.value})} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                  <option value="M">Muški</option>
                  <option value="Z">Ženski</option>
                </select></div>
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">4. Osnov prava korišćenja objekta</h4>
            <div className="grid grid-cols-3 gap-3">
              <select value={form.ctr_osnov_koriscenja} onChange={e => setForm({...form, ctr_osnov_koriscenja: e.target.value})} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                <option value="svojina">Svojina</option>
                <option value="zakup">Zakup</option>
                <option value="drugi">Drugi pravni osnov</option>
              </select>
              {form.ctr_osnov_koriscenja === 'drugi' && (
                <div className="col-span-2"><Input value={form.ctr_osnov_drugi} onChange={e => setForm({...form, ctr_osnov_drugi: e.target.value})} placeholder="Navesti osnov..." className="bg-white border-slate-300 text-sm"/></div>
              )}
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">5. Objekat - kat. podaci</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Opština i mjesto</Label>
                <Input value={form.ctr_obj_opstina_mjesto} onChange={e => setForm({...form, ctr_obj_opstina_mjesto: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Kat. opština</Label>
                <Input value={form.ctr_obj_kat_opstina} onChange={e => setForm({...form, ctr_obj_kat_opstina: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Kat. parcela</Label>
                <Input value={form.ctr_obj_kat_parcela} onChange={e => setForm({...form, ctr_obj_kat_parcela: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Kat. lokacija</Label>
                <Input value={form.ctr_obj_kat_lokacija} onChange={e => setForm({...form, ctr_obj_kat_lokacija: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Površina objekta (m²)</Label>
                <Input value={form.ctr_obj_povrsina} onChange={e => setForm({...form, ctr_obj_povrsina: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">6. Lokacija/mjesto</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                ['ctr_lokacija_grad','U gradu'],['ctr_lokacija_prigrad','U prigradskom području'],
                ['ctr_lokacija_plaza','U blizini plaže'],['ctr_lokacija_skijaliste','U blizini skijališta'],
                ['ctr_lokacija_nacionalni_park','U blizini nacionalnog parka'],
              ].map(([k,l]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!form[k]} onChange={e => setForm({...form, [k]: e.target.checked})}/>
                  {l}
                </label>
              ))}
            </div>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Ostalo (upisati lokaciju)</Label>
              <Input value={form.ctr_lokacija_ostalo} onChange={e => setForm({...form, ctr_lokacija_ostalo: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">7. Odgovorno lice u objektu</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Ime i prezime</Label>
                <FizickoLiceAutocomplete
                  value={form.ctr_odg_objekat_ime}
                  onChange={v => setForm({...form, ctr_odg_objekat_ime: v})}
                  onSelect={handleFlOdgObjekat}
                  clientId={form.client_id}
                  testid="ctr-odg-obj-ime-input"
                /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">JMB</Label>
                <Input value={form.ctr_odg_objekat_jmb} onChange={e => setForm({...form, ctr_odg_objekat_jmb: e.target.value.replace(/\D/g,'').slice(0,13)})} maxLength={13} className="bg-white border-slate-300 text-sm font-mono"/></div>
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">8. Zaposleni</h4>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div></div><div className="font-semibold text-slate-600">Stalno</div><div className="font-semibold text-slate-600">Povremeno</div><div></div>
              <div className="font-semibold text-slate-600 self-center">Žene</div>
              <Input value={form.ctr_zap_zene_stalno} onChange={e => setForm({...form, ctr_zap_zene_stalno: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
              <Input value={form.ctr_zap_zene_povremeno} onChange={e => setForm({...form, ctr_zap_zene_povremeno: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
              <div></div>
              <div className="font-semibold text-slate-600 self-center">Muškarci</div>
              <Input value={form.ctr_zap_musk_stalno} onChange={e => setForm({...form, ctr_zap_musk_stalno: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
              <Input value={form.ctr_zap_musk_povremeno} onChange={e => setForm({...form, ctr_zap_musk_povremeno: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
              <div></div>
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">9. Period rada</h4>
            <select value={form.ctr_period} onChange={e => setForm({...form, ctr_period: e.target.value})} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
              <option value="cijela_godina">Tokom cijele godine</option>
              <option value="sezonski_ljeti">Sezonski - ljeti</option>
              <option value="sezonski_zimi">Sezonski - zimi</option>
            </select>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">10. Smještajne jedinice</h4>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div></div><div className="font-semibold text-slate-600">Sobe</div><div className="font-semibold text-slate-600">Apartmani</div><div className="font-semibold text-slate-600">Ležaji</div>
              <div className="font-semibold text-slate-600 self-center">U objektu</div>
              <Input value={form.ctr_obj_broj_soba} onChange={e => setForm({...form, ctr_obj_broj_soba: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
              <Input value={form.ctr_obj_broj_apart} onChange={e => setForm({...form, ctr_obj_broj_apart: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
              <Input value={form.ctr_obj_broj_lezaja} onChange={e => setForm({...form, ctr_obj_broj_lezaja: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
              <div className="font-semibold text-slate-600 self-center">U depadansu</div>
              <Input value={form.ctr_dep_broj_soba} onChange={e => setForm({...form, ctr_dep_broj_soba: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
              <Input value={form.ctr_dep_broj_apart} onChange={e => setForm({...form, ctr_dep_broj_apart: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
              <Input value={form.ctr_dep_broj_lezaja} onChange={e => setForm({...form, ctr_dep_broj_lezaja: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">11. Restoran/i</h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Vrsta</Label>
                <Input value={form.ctr_restoran_vrsta} onChange={e => setForm({...form, ctr_restoran_vrsta: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Broj</Label>
                <Input value={form.ctr_restoran_broj} onChange={e => setForm({...form, ctr_restoran_broj: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Kapacitet (sjedišta)</Label>
                <Input value={form.ctr_restoran_kapacitet} onChange={e => setForm({...form, ctr_restoran_kapacitet: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">12. Sadržaji</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                ['ctr_sadrzaj_kongres','Kongresna sala'],['ctr_sadrzaj_spa','Velnes/spa'],
                ['ctr_sadrzaj_kazino','Kazino'],['ctr_sadrzaj_tenis','Teniski teren'],
                ['ctr_sadrzaj_kosarka','Košarkaški teren'],['ctr_sadrzaj_odbojka','Tereni za odbojku'],
                ['ctr_sadrzaj_bazen_otv','Otvoreni bazen'],['ctr_sadrzaj_bazen_zat','Zatvoreni bazen'],
              ].map(([k,l]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!form[k]} onChange={e => setForm({...form, [k]: e.target.checked})}/>
                  {l}
                </label>
              ))}
            </div>
            <Input value={form.ctr_sadrzaj_ostalo} onChange={e => setForm({...form, ctr_sadrzaj_ostalo: e.target.value})} placeholder="Ostali sadržaji..." className="bg-white border-slate-300 text-sm"/>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">13. Parking prostor</h4>
            <div className="grid grid-cols-4 gap-2 text-xs">
              {[['ctr_parking_otv','Otvoreni'],['ctr_parking_natkr','Natkriveni'],['ctr_parking_garaza','Garaža'],['ctr_parking_neob','Neobilježeni']].map(([k,l]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs text-slate-500 uppercase">{l}</Label>
                  <Input value={form[k]} onChange={e => setForm({...form, [k]: e.target.value.replace(/\D/g,'')})} className="bg-white border-slate-300 text-sm"/>
                </div>
              ))}
            </div>

            <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-3">Napomena i potpis</h4>
            <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Napomena (ostale informacije/komentar)</Label>
              <textarea value={form.ctr_napomena} onChange={e => setForm({...form, ctr_napomena: e.target.value})} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[60px]"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Lice odgovorno za tačnost podataka</Label>
                <Input value={form.ctr_lice_odg_podaci} onChange={e => setForm({...form, ctr_lice_odg_podaci: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500 uppercase">Mjesto potpisa (grad)</Label>
                <Input value={form.ctr_mjesto_potpisa} onChange={e => setForm({...form, ctr_mjesto_potpisa: e.target.value})} className="bg-white border-slate-300 text-sm"/></div>
            </div>

            <div className="space-y-1 pt-3 border-t"><Label className="text-xs text-slate-500 uppercase">Datum podnošenja</Label>
              <DateInput value={form.datum_podnosenja} onChange={e => setForm({...form, datum_podnosenja: e.target.value})} className="border-slate-300 text-sm"/></div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button variant="outline" onClick={()=>setIsFormOpen(false)}>Odustani</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">{editingId?'Ažuriraj':'Kreiraj'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
